import { SupportedCurrencyCodesSchema, } from "./currencies.js";
/**
 * Best-effort IANA-timezone → default-currency mapping, used to pre-fill a
 * new user's preferred currency before they have set one explicitly. This is
 * only a starting suggestion: the user can change it in settings, and the
 * value is never authoritative for any money math.
 *
 * Keyed by the full IANA zone id (e.g. `"Europe/Paris"`). Zones not listed
 * fall back through {@link currencyForTimeZone}'s region heuristic and finally
 * to USD, so the table only needs the common zones — not every entry in the
 * tz database.
 */
const CURRENCY_BY_TIME_ZONE = {
    // Americas
    "America/New_York": "USD",
    "America/Detroit": "USD",
    "America/Chicago": "USD",
    "America/Denver": "USD",
    "America/Phoenix": "USD",
    "America/Los_Angeles": "USD",
    "America/Anchorage": "USD",
    "Pacific/Honolulu": "USD",
    "America/Toronto": "CAD",
    "America/Vancouver": "CAD",
    "America/Edmonton": "CAD",
    "America/Winnipeg": "CAD",
    "America/Halifax": "CAD",
    "America/Mexico_City": "MXN",
    "America/Tijuana": "MXN",
    "America/Sao_Paulo": "BRL",
    "America/Bahia": "BRL",
    "America/Argentina/Buenos_Aires": "ARS",
    "America/Santiago": "CLP",
    "America/Bogota": "COP",
    "America/Lima": "PEN",
    "America/Caracas": "VES",
    "America/Montevideo": "UYU",
    "America/Asuncion": "PYG",
    "America/La_Paz": "BOB",
    "America/Guatemala": "GTQ",
    "America/Costa_Rica": "CRC",
    "America/Panama": "PAB",
    "America/Santo_Domingo": "DOP",
    "America/Jamaica": "JMD",
    // Europe
    "Europe/London": "GBP",
    "Europe/Dublin": "EUR",
    "Europe/Paris": "EUR",
    "Europe/Berlin": "EUR",
    "Europe/Madrid": "EUR",
    "Europe/Rome": "EUR",
    "Europe/Amsterdam": "EUR",
    "Europe/Brussels": "EUR",
    "Europe/Vienna": "EUR",
    "Europe/Lisbon": "EUR",
    "Europe/Athens": "EUR",
    "Europe/Helsinki": "EUR",
    "Europe/Zurich": "CHF",
    "Europe/Oslo": "NOK",
    "Europe/Stockholm": "SEK",
    "Europe/Copenhagen": "DKK",
    "Europe/Warsaw": "PLN",
    "Europe/Prague": "CZK",
    "Europe/Budapest": "HUF",
    "Europe/Bucharest": "RON",
    "Europe/Sofia": "BGN",
    "Europe/Moscow": "RUB",
    "Europe/Kyiv": "UAH",
    "Europe/Kiev": "UAH",
    "Europe/Istanbul": "TRY",
    // Africa & Middle East
    "Africa/Cairo": "EGP",
    "Africa/Lagos": "NGN",
    "Africa/Nairobi": "KES",
    "Africa/Johannesburg": "ZAR",
    "Africa/Casablanca": "MAD",
    "Africa/Accra": "GHS",
    "Africa/Algiers": "DZD",
    "Africa/Tunis": "TND",
    "Asia/Jerusalem": "ILS",
    "Asia/Tel_Aviv": "ILS",
    "Asia/Riyadh": "SAR",
    "Asia/Dubai": "AED",
    "Asia/Qatar": "QAR",
    "Asia/Kuwait": "KWD",
    "Asia/Bahrain": "BHD",
    "Asia/Muscat": "OMR",
    "Asia/Amman": "JOD",
    "Asia/Beirut": "LBP",
    "Asia/Tehran": "IRR",
    "Asia/Baghdad": "IQD",
    // Asia & Pacific
    "Asia/Kolkata": "INR",
    "Asia/Calcutta": "INR",
    "Asia/Karachi": "PKR",
    "Asia/Dhaka": "BDT",
    "Asia/Colombo": "LKR",
    "Asia/Kathmandu": "NPR",
    "Asia/Shanghai": "CNY",
    "Asia/Hong_Kong": "HKD",
    "Asia/Taipei": "TWD",
    "Asia/Tokyo": "JPY",
    "Asia/Seoul": "KRW",
    "Asia/Singapore": "SGD",
    "Asia/Kuala_Lumpur": "MYR",
    "Asia/Bangkok": "THB",
    "Asia/Jakarta": "IDR",
    "Asia/Manila": "PHP",
    "Asia/Ho_Chi_Minh": "VND",
    "Asia/Saigon": "VND",
    "Asia/Almaty": "KZT",
    "Asia/Tashkent": "UZS",
    "Australia/Sydney": "AUD",
    "Australia/Melbourne": "AUD",
    "Australia/Perth": "AUD",
    "Australia/Brisbane": "AUD",
    "Pacific/Auckland": "NZD",
    "Pacific/Fiji": "FJD",
};
/**
 * First-segment region → fallback currency. Used when the exact zone is not
 * in {@link CURRENCY_BY_TIME_ZONE} (e.g. a zone we don't list explicitly), so
 * "Europe/Somewhere" still suggests EUR rather than USD.
 */
const FALLBACK_BY_REGION = {
    Europe: "EUR",
    Australia: "AUD",
};
const DEFAULT_CURRENCY = "USD";
/**
 * Suggests a default currency for an IANA `timeZone` id (such as the value of
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`). Falls back to a
 * region-level guess, then to USD. Never throws on an unknown or empty input.
 */
export function currencyForTimeZone(timeZone) {
    if (!timeZone)
        return DEFAULT_CURRENCY;
    const exact = CURRENCY_BY_TIME_ZONE[timeZone];
    if (exact)
        return exact;
    const region = timeZone.split("/")[0] ?? "";
    return FALLBACK_BY_REGION[region] ?? DEFAULT_CURRENCY;
}
/**
 * Resolves the preferred currency to display for a user: their explicit
 * `preferredCurrency` if set and valid, otherwise the timezone-derived
 * suggestion. Centralised so web and mobile apply the same precedence.
 */
export function resolvePreferredCurrency(preferredCurrency, timeZone) {
    const parsed = SupportedCurrencyCodesSchema.safeParse(preferredCurrency);
    if (parsed.success)
        return parsed.data;
    return currencyForTimeZone(timeZone);
}
/**
 * The runtime's IANA time-zone id (e.g. `"Europe/Paris"`), or `undefined` if
 * the environment can't report one. Wraps `Intl` so the web and mobile
 * clients share one definition instead of each re-implementing the try/catch.
 */
export function localTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    }
    catch {
        return undefined;
    }
}
